import React from "react";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { FAQSchema } from "@/components/seo/schemas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, Calendar, Mail } from "lucide-react";
import { BOOKING_URL, CONTACT_EMAIL } from "@/config/constants";
import StickyMobileCTA from "@/components/StickyMobileCTA";

const OVERSIGHT_PACK = [
  {
    title: "Committee-Ready Slide Deck (10–14 slides)",
    desc: "A board packet–appropriate deck that frames AI oversight expectations, roles, controls, and what the committee should require from management.",
  },
  {
    title: "12 Questions the Chair Must Ask — Checklist (1 page)",
    desc: "A printable checklist for Audit/Risk Chairs to use in meetings—focused on accountability, controls, third‑party risk, cyber/privacy, incident response, and internal audit assurance.",
  },
  {
    title: "Next Steps Memo (1–2 pages)",
    desc: "A practical 30/60/90‑day action plan for management, including recommended owners and what should come back to the committee (and when).",
  },
  {
    title: "AI Oversight Dashboard Template (1 page) — Optional Add-On",
    desc: "A simple reporting template management can update monthly/quarterly (inventory status, tiered risks, incidents, vendor exposure, testing coverage, training adoption).",
  },
];

const AGENDA = [
  { title: "The Oversight Shift", desc: "What's different about AI risk—and why oversight expectations are rising now." },
  { title: "Material AI Exposure", desc: "Where AI becomes material: financial reporting, operational controls, customer impact, regulatory and reputational exposure." },
  { title: "Governance Model the Committee Should Require", desc: "Ownership, decision rights, escalation paths, policy baseline, and how AI fits into ERM." },
  { title: "Controls & Monitoring (Committee-Grade)", desc: "Pre‑deployment and post‑deployment controls: validation, monitoring, drift, logging, change management, human oversight, and incident response." },
  { title: "Third-Party & Vendor AI Risk", desc: "Due diligence, ongoing monitoring, contractual expectations, and go/no-go governance." },
  { title: "Assurance & Reporting Cadence", desc: "What management should report quarterly, what Internal Audit should test, and what triggers committee escalation." },
];

const CHAIR_QUESTIONS = [
  "Where are we using AI today—and where is it planned next? Do we have an inventory we trust?",
  "Which AI uses are material (financial reporting impact, regulatory exposure, customer harm, safety, mission-critical operations)?",
  "Who is accountable for AI risk? One named executive—and how does it integrate into ERM?",
  "What is our AI risk appetite? What do we explicitly allow, restrict, and prohibit—and who decides?",
  "What controls exist before deployment (validation, approvals, privacy/security review, documentation)?",
  "What controls exist after deployment (monitoring, drift detection, logging, rollback procedures, incident triggers)?",
  "Do we have auditability and traceability (logs, documentation, ability to explain outcomes)?",
  "How are third‑party AI vendors governed (due diligence, monitoring, contractual protections, exit plans)?",
  "How are data and privacy risks handled (data provenance, sensitive data handling, retention, access controls)?",
  "How does cybersecurity intersect with AI (prompt injection, data leakage, model/system compromise, supply chain risk)?",
  "What is Internal Audit's plan for AI assurance?",
  "What triggers immediate committee notification (AI incidents, regulatory inquiries, customer harm, major control failures)?",
];

const PROCESS_STEPS = [
  { title: "Initial Chair Call", desc: "Align on committee mandate, current concerns, and desired outcomes." },
  { title: "Quick Intake", desc: "A short questionnaire to tailor the deck and checklist to your context. (Optional) Share any existing AI policy, AI inventory, vendor list, or risk register excerpts." },
  { title: "Deliver the Briefing", desc: "Structured briefing + Q&A and committee action planning." },
  { title: "Receive Your Oversight Pack", desc: "Deck + Chair checklist + Next Steps memo (+ optional dashboard template)." },
];

const TRIGGERS = [
  "We don't know where AI is being used across the business.",
  "We need a clear AI governance owner and escalation path.",
  "Internal Audit is being asked to audit AI, but scope and criteria are unclear.",
  "We're buying AI-enabled vendor tools and need defensible third‑party oversight.",
  "Cyber/privacy leaders are concerned about AI data leakage and new attack paths.",
  "The committee needs a reporting cadence that's meaningful—not noise.",
];

const SPRINTS = [
  { title: "AI Governance Readiness Sprint", desc: "Inventory + tiering + governance operating model + 30/60/90 roadmap" },
  { title: "AI Vendor Risk Review", desc: "Vendor scorecard + control requirements + go/no-go memo" },
  { title: "Internal Audit AI Assurance Plan", desc: "Scoped audit approach, coverage map, and reporting structure" },
];

const FAQS = [
  { question: "Is this legal advice or an audit opinion?", answer: "No. This is governance advisory support to help committees structure their AI oversight posture. Involve counsel for jurisdiction-specific legal interpretation." },
  { question: "Do you need access to systems or proprietary data?", answer: "No. We work from policy documents, risk registers, and committee-level information. No system access required." },
  { question: "Can management attend?", answer: "Yes. Many committees include key management (GC, CRO, CISO, Chief Audit Executive) to align on oversight expectations and action items." },
  { question: "Is this only for public companies?", answer: "No. Any organization with a board or oversight body responsible for enterprise risk can benefit—public, private, non-profit, or government." },
  { question: "Can you tailor to our regulatory footprint and frameworks?", answer: "Absolutely. We tailor to your jurisdiction, sector, and the frameworks your organization already uses (e.g., NIST AI RMF, ISO 42001, EU AI Act, OSFI, APRA)." },
  { question: "How quickly can this be scheduled?", answer: "Most briefings can be scheduled within 2–3 weeks. We accommodate committee calendars and can work around board meeting schedules." },
  { question: "What happens after the briefing?", answer: "You receive the Oversight Pack. Many committees use it as the launch point for a short, fixed-scope sprint to operationalize governance and controls." },
];

const SECTION_IDS = [
  { id: "oversight-pack", label: "Oversight Pack" },
  { id: "agenda", label: "Agenda" },
  { id: "chair-questions", label: "Chair Questions" },
  { id: "process", label: "Process" },
  { id: "sprints", label: "Sprints" },
  { id: "faq", label: "FAQ" },
];

const BoardBriefingsPage: React.FC = () => {
  return (
    <Layout>
      <PageMeta
        title="Board Briefings | BoardSight — Defensible AI Oversight for Audit & Risk Committees"
        description="Committee-ready AI oversight briefings with a slide deck, chair checklist, and 30/60/90-day action plan. Not generic AI training—oversight enablement."
        canonicalUrl="https://boardsight.ai/board-briefings"
      />
      <FAQSchema faqs={FAQS} />
      <StickyMobileCTA />

      {/* Hero */}
      <section className="pt-16 pb-20 md:pt-24 md:pb-28 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Defensible AI Oversight for Audit &amp; Risk Committees
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-4 max-w-3xl mx-auto">
              Audit and Risk Committees are being asked to oversee AI like any other enterprise risk: with clear accountability, controls, reporting, and assurance.
            </p>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
              This briefing equips your committee with a chair-ready checklist, a board-ready slide deck, and a 30/60/90-day action plan for management—so oversight becomes practical, not theoretical.
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Book a Chair Call
              </a>
            </Button>
            <p className="text-sm text-muted-foreground mt-6">
              Built for Audit Committee Chairs, Risk Committee Chairs, and Directors overseeing ERM, compliance, cyber/privacy, third‑party risk, and internal audit.
            </p>
          </div>
        </div>
      </section>

      {/* Mini anchor nav */}
      <nav className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container-custom py-3 overflow-x-auto">
          <div className="flex gap-4 min-w-max">
            {SECTION_IDS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="text-sm text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
              >
                {s.label}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Oversight Pack */}
      <section id="oversight-pack" className="py-16 md:py-24 scroll-mt-16">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
              A committee-ready Oversight Pack
            </h2>
            <p className="text-lg text-muted-foreground mb-10 text-center">
              This isn't generic AI training. It's oversight enablement for Audit &amp; Risk Committees—built around governance, controls, assurance, and reporting cadence.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {OVERSIGHT_PACK.map((item, i) => (
                <Card key={i} className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg font-bold text-foreground">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Designed for */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
              Designed for Audit &amp; Risk Committee leadership
            </h2>
            <p className="text-lg text-muted-foreground mb-6 text-center">
              This briefing is ideal for organizations where:
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "The Audit Committee (or a combined Audit/Risk Committee) has AI oversight responsibility",
                "AI use is expanding across the business faster than governance can keep up",
                "Management needs clarity on what good looks like for controls, documentation, and reporting",
                "Internal Audit is being asked to provide assurance on AI governance and controls",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <div className="bg-card border border-border rounded-lg p-6">
              <p className="text-sm font-semibold text-foreground mb-2">Typical Attendees</p>
              <p className="text-sm text-muted-foreground">
                Audit Committee Chair · Risk Committee Chair · Board Directors · General Counsel · CRO · CISO · Chief Audit Executive · Compliance · Data/AI leadership (as appropriate)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Agenda */}
      <section id="agenda" className="py-16 md:py-24 scroll-mt-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
              An agenda built for Audit &amp; Risk Committees
            </h2>
            <p className="text-lg text-muted-foreground mb-10 text-center">
              Structured to address the oversight questions that matter most
            </p>
            <div className="space-y-6">
              {AGENDA.map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 12 Chair Questions — Accordion on mobile */}
      <section id="chair-questions" className="py-16 md:py-24 bg-muted/30 scroll-mt-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2 text-center">
              The Chair's AI Oversight Questions
            </h2>
            <p className="text-lg text-muted-foreground mb-10 text-center">
              Audit &amp; Risk Committee Edition
            </p>

            {/* Desktop: numbered list */}
            <ol className="hidden md:block space-y-4">
              {CHAIR_QUESTIONS.map((q, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </span>
                  <span className="text-foreground pt-1">{q}</span>
                </li>
              ))}
            </ol>

            {/* Mobile: accordion */}
            <Accordion type="single" collapsible className="md:hidden w-full">
              {CHAIR_QUESTIONS.map((q, i) => (
                <AccordionItem key={i} value={`q-${i}`}>
                  <AccordionTrigger className="text-left text-foreground text-sm">
                    <span className="flex items-center gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                        {i + 1}
                      </span>
                      <span className="line-clamp-1">{q.split("?")[0]}?</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pl-10">
                    {q}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="py-16 md:py-24 scroll-mt-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
              Simple, fast, and built for committee schedules
            </h2>
            <p className="text-lg text-muted-foreground mb-10 text-center">
              Four steps from initial call to actionable oversight
            </p>
            <div className="space-y-6">
              {PROCESS_STEPS.map((step, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground">{step.title}</h3>
                    <p className="text-muted-foreground">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Triggers */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              If any of these are happening, this briefing is for you.
            </h2>
            <ul className="space-y-4">
              {TRIGGERS.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Implementation Sprints */}
      <section id="sprints" className="py-16 md:py-24 scroll-mt-16">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
              Need implementation support? We can extend this into a sprint.
            </h2>
            <p className="text-lg text-muted-foreground mb-10 text-center">
              Many committees use the briefing as the launch point for a short, fixed-scope engagement to operationalize governance and controls.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {SPRINTS.map((sprint, i) => (
                <Card key={i} className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-lg font-bold text-foreground">{sprint.title}</CardTitle>
                    <p className="text-sm text-muted-foreground">Custom</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{sprint.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 md:py-24 bg-muted/30 scroll-mt-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-foreground hover:text-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 mb-16 md:mb-0">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Make AI oversight practical—before it becomes urgent.
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              If your Audit or Risk Committee is being asked where the AI oversight model is, this briefing gives you a defensible answer and a clear path forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Book a Chair Call
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {CONTACT_EMAIL}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BoardBriefingsPage;
